import { useEffect, useState, useCallback } from "react";
import { Loader2, Moon, CalendarRange, RefreshCw, CheckCircle2, AlertTriangle, Hourglass, PauseCircle } from "lucide-react";
import { api } from "@/lib/api";

/** Convert ISO string → "3m ago" / "2h ago" / "5d ago". */
function formatAgo(iso) {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const secs = Math.max(0, Math.round((Date.now() - t) / 1000));
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.round(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.round(secs / 3600)}h ago`;
  return `${Math.round(secs / 86400)}d ago`;
}

const STATUS_STYLES = {
  ok:             { cls: "bg-emerald-100 text-emerald-800", Icon: CheckCircle2, label: "ok" },
  running:        { cls: "bg-sky-100 text-sky-800",         Icon: Hourglass,    label: "running" },
  budget:         { cls: "bg-amber-100 text-amber-800",     Icon: PauseCircle,  label: "budget" },
  nothing_to_do:  { cls: "bg-stone-100 text-stone-700",     Icon: PauseCircle,  label: "idle" },
  error:          { cls: "bg-rose-100 text-rose-900",       Icon: AlertTriangle,label: "error" },
  // Written by the server-side orphan sweep: a daemon whose process
  // was killed before it could flip status, so the row sat at
  // "running" indefinitely. Rendered in rose so it stands out as
  // "needs attention", distinct from a hard "error" return.
  orphaned:       { cls: "bg-rose-50 text-rose-800 border border-rose-200", Icon: AlertTriangle, label: "orphaned" },
  // SIGTERM pathway — supervisor stopped us (pod restart, cron off),
  // but the daemon flushed its row cleanly. Not an error, just an
  // interrupted run; rendered in stone so it reads "fine but partial".
  aborted:        { cls: "bg-stone-200 text-stone-700", Icon: PauseCircle, label: "aborted" },
};

function StatusBadge({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.ok;
  const { Icon } = s;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${s.cls}`}>
      <Icon className="h-3 w-3" />
      {s.label}
    </span>
  );
}

function fmtCounts(counts) {
  if (!counts || typeof counts !== "object") return "";
  const bits = [];
  if (typeof counts.ok === "number")       bits.push(`ok ${counts.ok}`);
  if (typeof counts.bad === "number")      bits.push(`bad ${counts.bad}`);
  if (typeof counts.budget === "number")   bits.push(`budget ${counts.budget}`);
  if (typeof counts.attempted === "number") bits.push(`of ${counts.attempted}`);
  return bits.join(" · ");
}

function fmtDuration(secs) {
  if (typeof secs !== "number" || !isFinite(secs)) return "—";
  if (secs < 90) return `${Math.round(secs)}s`;
  if (secs < 5400) return `${Math.round(secs / 60)}m`;
  return `${(secs / 3600).toFixed(1)}h`;
}

/**
 * NightlyHarvestTile — Progress Dash tile that answers two questions:
 *   1. Which theme is running tonight (and what's queued for the next 7
 *      nights)?
 *   2. How did the last few nightly runs do (rows written, budget hits,
 *      errors)?
 *
 * Backed by GET /api/admin/progress/nightly-harvest. Themed schedule
 * is single-sourced in /app/backend/data/nightly_schedule.py.
 */
export default function NightlyHarvestTile() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/progress/nightly-harvest");
      setData(r.data);
      setErr(null);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load nightly harvest");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, [load]);

  if (loading && !data) {
    return (
      <div
        className="border border-stone-200 rounded-xl p-5 bg-white shadow-sm text-sm text-stone-500"
        data-testid="nightly-harvest-tile"
      >
        <Loader2 className="inline h-4 w-4 animate-spin mr-1" /> Loading nightly harvest…
      </div>
    );
  }
  if (err) {
    return (
      <div
        className="border border-rose-200 bg-rose-50/50 rounded-xl p-4 text-sm text-rose-900"
        data-testid="nightly-harvest-tile"
      >
        Nightly harvest failed: {err}
      </div>
    );
  }
  if (!data) return null;

  const { tonight, upcoming, recent_runs: recent, nightly_window_utc: win } = data;

  return (
    <div
      className="border border-stone-200 rounded-xl bg-white shadow-sm overflow-hidden"
      data-testid="nightly-harvest-tile"
    >
      {/* Header strip */}
      <div className="flex items-center justify-between gap-3 px-5 py-3 bg-gradient-to-r from-[#1E1B4B] via-[#3B0764] to-[#4C1D95] text-white">
        <div className="flex items-center gap-2">
          <Moon className="h-4 w-4" />
          <div className="text-[11px] font-underground-sc tracking-widest uppercase opacity-80">
            Nightly harvest
          </div>
        </div>
        <button
          type="button"
          onClick={load}
          className="inline-flex items-center gap-1 text-[11px] opacity-80 hover:opacity-100"
          data-testid="nightly-harvest-refresh"
        >
          <RefreshCw className="h-3 w-3" />
          Refresh
        </button>
      </div>

      {/* Tonight */}
      <div className="px-5 py-4 border-b border-stone-100">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10px] font-underground-sc tracking-widest text-stone-500 uppercase">
              Tonight · {tonight.weekday}
            </div>
            <div
              className="mt-0.5 text-lg font-semibold text-stone-900"
              data-testid="nightly-harvest-tonight-theme"
            >
              {tonight.is_rest ? "Rest night" : tonight.theme}
            </div>
            {!tonight.is_rest && (
              <div className="mt-1 text-[12px] text-stone-600 max-w-xl leading-snug">
                {tonight.summary}
              </div>
            )}
          </div>
          <div className="text-right text-[11px] text-stone-600 font-mono">
            <div>Starts {win.start} UTC</div>
            <div>Stops {win.stop} UTC</div>
          </div>
        </div>
        {!tonight.is_rest && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {tonight.daemons.map((d) => (
              <span
                key={d}
                className="inline-flex items-center rounded-md bg-[#F5F3FF] text-[#4C1D95] px-2 py-0.5 text-[11px] font-mono"
              >
                {d}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Upcoming 7 nights */}
      <div className="px-5 py-4 border-b border-stone-100">
        <div className="flex items-center gap-2 text-[10px] font-underground-sc tracking-widest text-stone-500 uppercase mb-2">
          <CalendarRange className="h-3.5 w-3.5" />
          Next 7 nights
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
          {upcoming.map((n, i) => (
            <div
              key={n.date}
              className={`rounded-lg border p-2 text-[11px] leading-tight ${
                i === 0
                  ? "border-[#4C1D95]/40 bg-[#F5F3FF]"
                  : "border-stone-200 bg-stone-50"
              }`}
              data-testid={`nightly-harvest-upcoming-${i}`}
            >
              <div className="font-mono text-[10px] text-stone-500">{n.weekday}</div>
              <div className="font-semibold text-stone-900 mt-0.5 truncate" title={n.theme}>
                {n.is_rest ? "Rest" : n.theme}
              </div>
              <div className="mt-0.5 text-[10px] text-stone-500">
                {n.daemons.length} daemon{n.daemons.length === 1 ? "" : "s"}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent runs */}
      <div className="px-5 py-4">
        <div className="text-[10px] font-underground-sc tracking-widest text-stone-500 uppercase mb-2">
          Last {recent.length} runs
        </div>
        {recent.length === 0 ? (
          <div className="text-[12px] text-stone-500 italic">
            No daemon runs recorded yet. The next scheduled run will show up here once it starts.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-[11px]">
              <thead className="text-stone-500">
                <tr>
                  <th className="pr-3 pb-1 font-semibold">Daemon</th>
                  <th className="pr-3 pb-1 font-semibold">Started</th>
                  <th className="pr-3 pb-1 font-semibold">Duration</th>
                  <th className="pr-3 pb-1 font-semibold">Status</th>
                  <th className="pb-1 font-semibold">Counts</th>
                </tr>
              </thead>
              <tbody className="text-stone-800 font-mono">
                {recent.map((r, i) => (
                  <tr key={i} className="border-t border-stone-100">
                    <td className="pr-3 py-1.5">{r.name}</td>
                    <td className="pr-3 py-1.5" title={r.started_at || ""}>
                      {formatAgo(r.started_at)}
                    </td>
                    <td className="pr-3 py-1.5">{fmtDuration(r.duration_s)}</td>
                    <td className="pr-3 py-1.5">
                      <StatusBadge status={r.status} />
                    </td>
                    <td className="py-1.5 text-stone-600">{fmtCounts(r.counts) || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
